<script>
import { GlBadge, GlLink, GlSprintf, GlAlert } from '@gitlab/ui';
import ErrorAlert from '~/javascripts/components/error_alert.vue';
import PurchaseSuccessModal from '~/javascripts/purchase/components/purchase_success_modal.vue';
import ZuoraAvailabilityWrapper from '~/javascripts/components/zuora_availability_wrapper.vue';
import GroupDetailsHeader from '~/javascripts/subscription/components/subscription/group_details_header.vue';
import { getSubscriptionTitle } from '~/javascripts/subscription/components/subscription/utils';
import { PRODUCT_SUB_TYPES } from '~/javascripts/subscription/components/subscription/constants';
import { ADDON_PRODUCT_TYPE, GITLAB_EE_PLAN_TYPE } from '~/javascripts/subscription/constants';
import {
  ERROR_PROCESSING_PAYMENT_TYPE,
  PURCHASE_ERROR_DICTIONARY,
} from '~/javascripts/purchase/error_dictionary';
import { MANUAL_SUBSCRIPTION_SYNC_DOCS_URL } from '~/javascripts/constants';

import DuoProSeatsForm from './duo_pro_seats_form.vue';

export const i18n = Object.freeze({
  MODAL_TITLE: 'Purchase complete',
  MODAL_ACTION_PRIMARY: { text: 'Manage purchases' },
  MODAL_DESCRIPTION_SAAS: 'Your seats purchase has been successfully processed.',
  MODAL_DESCRIPTION_SM: `Your purchase was processed successfully. Your subscription data will synchronize with your instance automatically, or you can %{linkStart}manually synchronize%{linkEnd} your subscription details.`,
});
export default {
  name: 'BuyDuoProSeats',
  components: {
    ErrorAlert,
    GlAlert,
    GlBadge,
    GlLink,
    GlSprintf,
    GroupDetailsHeader,
    DuoProSeatsForm,
    PurchaseSuccessModal,
    ZuoraAvailabilityWrapper,
  },
  inject: ['subscription'],
  props: {
    subscriptionsPath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showSuccessModal: false,
      error: null,
    };
  },
  computed: {
    products() {
      return this.subscription.products ?? [];
    },
    addonProducts() {
      return this.products.filter((product) => product.type === ADDON_PRODUCT_TYPE);
    },
    mainProductRatePlans() {
      return this.products.filter((product) => product.type === PRODUCT_SUB_TYPES.MAIN);
    },
    mainProduct() {
      return this.mainProductRatePlans[0] ?? {};
    },
    subscriptionTitle() {
      return getSubscriptionTitle({
        namespace: this.subscription.namespace,
        mainProduct: this.mainProduct,
        addonProducts: this.addonProducts,
      });
    },
    duoProProduct() {
      return (
        this.products.find((product) => product.sub_type === PRODUCT_SUB_TYPES.CODE_SUGGESTIONS) ??
        null
      );
    },
    currentSeats() {
      return this.duoProProduct ? this.duoProProduct.quantity : 0;
    },
    isSM() {
      return this.subscription.type === GITLAB_EE_PLAN_TYPE;
    },
    modalDescription() {
      return this.isSM
        ? this.$options.i18n.MODAL_DESCRIPTION_SM
        : this.$options.i18n.MODAL_DESCRIPTION_SAAS;
    },
  },
  methods: {
    handleError(error) {
      this.error = error;
    },
    showSuccess() {
      this.showSuccessModal = true;
    },
  },
  i18n,
  MANUAL_SUBSCRIPTION_SYNC_DOCS_URL,
  PRODUCT_SUB_TYPES,
  PURCHASE_ERROR_DICTIONARY,
  defaultError: PURCHASE_ERROR_DICTIONARY[ERROR_PROCESSING_PAYMENT_TYPE],
};
</script>

<template>
  <div>
    <h1 class="gl-my-7 gl-font-bold">Buy GitLab Duo Pro</h1>
    <zuora-availability-wrapper>
      <error-alert
        v-if="error"
        :dismissible="false"
        :error-dictionary="$options.PURCHASE_ERROR_DICTIONARY"
        :error="error"
        :default-error="$options.defaultError"
        :placeholders="{
          namespace: subscription.namespace,
        }"
        data-testid="general-error-alert"
      />
      <div class="gl-mb-3 gl-flex gl-items-center gl-gap-3">
        <h2 data-testid="subscription-title">
          {{ subscriptionTitle }}
        </h2>
        <gl-badge variant="neutral" class="gl-items-start !gl-leading-normal"
          >{{ subscription.name }}
        </gl-badge>
      </div>
      <group-details-header />
      <gl-alert
        v-if="!subscription.hasOnlineCloudLicense && isSM"
        data-testid="offline-legacy-license-alert"
        class="gl-mt-5"
        variant="warning"
        :dismissible="false"
      >
        A synchronized subscription on GitLab 16.8 or later is required to use GitLab Duo.
      </gl-alert>
      <duo-pro-seats-form
        :current-seats="currentSeats"
        @error="handleError"
        @success="showSuccess"
      />
      <purchase-success-modal
        :title="$options.i18n.MODAL_TITLE"
        :action-primary="$options.i18n.MODAL_ACTION_PRIMARY"
        :visible="showSuccessModal"
        :success-redirect-path="subscriptionsPath"
      >
        <slot name="description">
          <gl-sprintf :message="modalDescription">
            <template v-if="isSM" #link="{ content }">
              <gl-link :href="$options.MANUAL_SUBSCRIPTION_SYNC_DOCS_URL" target="_blank">
                {{ content }}
              </gl-link>
            </template>
          </gl-sprintf>
        </slot>
      </purchase-success-modal>
    </zuora-availability-wrapper>
  </div>
</template>
